import { Link } from 'react-router-dom'
import { ExternalLink, TrendingUp, TrendingDown } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useDossiers, useDossiersLatestScores } from '@/hooks/useApi'
import type { Dossier, DossierLatestScores } from '@/api/client'
import { cn } from '@/lib/utils'

/**
 * One row per dossier: headline score, per-type score with its trend, GSC
 * figures and the latest security counts.
 *
 * Lifted out of the old Scan Center page. That page was folded into /scans
 * in June 2026, but this table was not carried over and its backing endpoint
 * (GET /api/dossiers/latest-scores) had no caller left. It is the same
 * overview the dashboard home is meant to get, so it lives as a component
 * rather than inline in a page.
 */

function scoreColor(s: number | null | undefined): string {
  if (s === null || s === undefined) return 'text-muted-foreground'
  return s >= 75 ? 'text-green-600 dark:text-green-300' : s >= 50 ? 'text-orange-600 dark:text-orange-300' : 'text-red-600 dark:text-red-300'
}

function Delta({ delta, invert = false }: { delta: number | null; invert?: boolean }) {
  if (delta === null || delta === 0) return null
  // invert: for "position", a drop (negative) is GOOD.
  const good = invert ? delta < 0 : delta > 0
  const Icon = delta > 0 ? TrendingUp : TrendingDown
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs', good ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300')}>
      <Icon className="h-3 w-3" />
      {Math.abs(Math.round(delta * 10) / 10)}
    </span>
  )
}

function ScoreCell({ entry }: { entry?: { score: number; prev: number | null } }) {
  if (!entry) return <span className="text-muted-foreground">—</span>
  const delta = entry.prev !== null && entry.prev !== undefined ? entry.score - entry.prev : null
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('font-semibold tabular-nums', scoreColor(entry.score))}>{entry.score}</span>
      <Delta delta={delta} />
    </span>
  )
}

export function DossierScoresTable() {
  const { data: dossiers, isLoading } = useDossiers({ active: true })
  const { data: latestScores } = useDossiersLatestScores()

  const list = dossiers ?? []
  const scoresById = new Map<number, DossierLatestScores['scores']>(
    (latestScores ?? []).map((r) => [r.dossier_id, r.scores]),
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scores &amp; trends</CardTitle>
        <CardDescription>
          Laatste scores per type (met trend t.o.v. vorige meting) + GSC en security per dossier.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex justify-center p-8"><Spinner /></div>
        ) : list.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Nog geen dossiers.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-xs text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">Dossier</th>
                  <th className="px-3 py-2 text-center font-medium">Headline</th>
                  <th className="px-3 py-2 text-center font-medium">On-page</th>
                  <th className="px-3 py-2 text-center font-medium">Site crawl</th>
                  <th className="px-3 py-2 text-center font-medium">Technisch</th>
                  <th className="px-3 py-2 text-right font-medium">GSC clicks</th>
                  <th className="px-3 py-2 text-right font-medium">Impressies</th>
                  <th className="px-3 py-2 text-right font-medium">Positie</th>
                  <th className="px-3 py-2 text-center font-medium">Security</th>
                  <th className="px-3 py-2 text-center font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {list.map((d: Dossier) => {
                  const sc = scoresById.get(d.id) ?? {}
                  const clickDelta =
                    d.last_gsc_clicks !== null && d.prev_gsc_clicks !== null && d.prev_gsc_clicks !== undefined
                      ? d.last_gsc_clicks - d.prev_gsc_clicks
                      : null
                  const crit = d.last_security_critical ?? 0
                  const high = d.last_security_high ?? 0
                  return (
                    <tr key={d.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2 font-medium">
                        <Link to={`/dossiers/${d.id}`} className="hover:text-primary hover:underline">
                          {d.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={cn('text-base font-bold tabular-nums', scoreColor(d.last_avg_seo_score))}>
                          {d.last_avg_seo_score ?? '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center"><ScoreCell entry={sc['onpage']} /></td>
                      <td className="px-3 py-2 text-center"><ScoreCell entry={sc['site_crawl']} /></td>
                      <td className="px-3 py-2 text-center"><ScoreCell entry={sc['tech_audit']} /></td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <span className="inline-flex items-center gap-1">
                          {d.last_gsc_clicks ?? '—'}
                          <Delta delta={clickDelta} />
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{d.last_gsc_impressions ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {d.last_gsc_position !== null && d.last_gsc_position !== undefined
                          ? d.last_gsc_position.toFixed(1)
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {crit > 0 ? (
                          <span className="font-medium text-red-600 dark:text-red-300">{crit} kritiek</span>
                        ) : high > 0 ? (
                          <span className="font-medium text-orange-600 dark:text-orange-300">{high} hoog</span>
                        ) : (
                          <span className="text-green-600 dark:text-green-300">✓</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Link to={`/dossiers/${d.id}`} className="text-muted-foreground hover:text-primary">
                          <ExternalLink className="mx-auto h-4 w-4" />
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
